import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { createBook, getAdminBook, updateBook, publishBook, unpublishBook, archiveBook, listMedia, mediaUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { ArrowLeft, Save, Eye, EyeOff, Archive, Plus, Trash2, AlertTriangle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  slug: "",
  title: "",
  subtitle: "",
  series: "Glories of India Series",
  topics: [],
  editions: ["English"],
  status: "In Development",
  is_featured: false,
  short_description: "",
  long_description: "",
  co_author: "",
  cover_url: "",
  cover_accent: "maroon",
  table_of_contents: [],
  why_read: [],
  what_readers_learn: [],
  practical_topics: [],
  research_themes: [],
  quotations: [],
  reviews: [],
  bulk_order_info: "",
  amazon_url: "",
  green_mall_url: "",
  goodreads_url: "",
  sample_pdf_url: "",
  trailer_url: "",
  trailer_thumbnail_url: "",
  related_book_slugs: [],
  seo_title: "",
  seo_meta_description: "",
  seo_social_image: "",
  seo_canonical_url: "",
  order: 999,
  editions_meta: {},
  translations: { hi: { status: "draft" }, bn: { status: "draft" } },
  publish_status: "draft",
  reservations_enabled: false,
  reservation_cta_label: "",
  reservation_opens_at: "",
  reservation_closes_at: "",
  reservation_expected_release_note: "",
  reservation_availability_status: "available",
};

const SERIES = ["Glories of India Series", "Green Guru Knowledge Series"];
const ACCENTS = ["maroon", "gold", "sandstone", "green"];
const STATUSES = ["Published", "Forthcoming", "In Development"];
const LANGUAGES = ["English", "Hindi", "Bengali"];
const SUBJECTS = [
  "History and Civilization", "Archaeology", "Comparative Civilizations", "Comparative Religion",
  "Public Awareness", "Colonial Historiography", "Environment", "Horticulture", "Gardening",
  "Landscaping", "Palms",
];

export default function AdminBookEditor() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [book, setBook] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [activeEditionTab, setActiveEditionTab] = useState("English");
  const [covers, setCovers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!isNew) {
      getAdminBook(id)
        .then(b => {
          if (cancelled) return;
          setBook({ ...EMPTY, ...b, translations: { ...EMPTY.translations, ...(b.translations || {}) } });
        })
        .catch(() => { if (!cancelled) toast.error("Book not found"); });
    }
    listMedia({ kind: "book_cover" })
      .then((data) => { if (!cancelled) setCovers(data); })
      .catch(() => { /* covers list is optional in the editor */ });
    return () => { cancelled = true; };
  }, [id, isNew]);

  const set = (k, v) => setBook(b => ({ ...b, [k]: v }));
  const setEditionMeta = (lang, k, v) => setBook(b => ({
    ...b,
    editions_meta: { ...b.editions_meta, [lang]: { ...(b.editions_meta?.[lang] || {}), [k]: v } },
  }));
  const setTranslation = (lang, k, v) => setBook(b => ({
    ...b,
    translations: { ...b.translations, [lang]: { ...(b.translations?.[lang] || {}), [k]: v } },
  }));

  const toggleEdition = (lang) => {
    setBook(b => {
      const has = b.editions.includes(lang);
      const editions = has ? b.editions.filter(x => x !== lang) : [...b.editions, lang];
      let editions_meta = { ...b.editions_meta };
      if (!has && !editions_meta[lang]) editions_meta[lang] = { title: b.title, cover_accent: b.cover_accent, status: "In Development" };
      return { ...b, editions, editions_meta };
    });
  };

  const hasPlaceholder = () => {
    const check = [book.title, book.short_description, book.long_description].some(v => (v || "").includes("[Editable"));
    return check;
  };

  const missingFields = () => {
    const missing = [];
    if (!book.title.trim()) missing.push("Title");
    if (!book.slug.trim()) missing.push("Slug");
    if (!book.short_description.trim()) missing.push("Short description");
    return missing;
  };

  const save = async () => {
    const m = missingFields();
    if (m.length) return toast.error("Missing required fields: " + m.join(", "));
    setSaving(true);
    try {
      const saved = isNew ? await createBook(book) : await updateBook(id, book);
      toast.success(isNew ? "Draft created" : "Saved");
      if (isNew) nav(`/admin/books/${saved.id}`);
      else setBook({ ...EMPTY, ...saved, translations: { ...EMPTY.translations, ...(saved.translations || {}) } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doPublish = async () => {
    if (hasPlaceholder()) {
      if (!window.confirm("This book still contains placeholder text (starting with '[Editable'). Publish anyway?")) return;
    }
    try { await publishBook(id); toast.success("Book published"); getAdminBook(id).then(setBook); } catch (e) { toast.error(e?.response?.data?.detail); }
  };
  const doUnpublish = async () => { try { await unpublishBook(id); toast.success("Unpublished"); getAdminBook(id).then(setBook); } catch { toast.error("Failed"); } };
  const doArchive = async () => { if (!window.confirm("Archive this book? It can be restored later.")) return; try { await archiveBook(id); toast.success("Archived"); nav("/admin/books"); } catch { toast.error("Failed"); } };

  const isOwner = user?.role === "owner";
  const isReviewer = user?.role === "reviewer";

  return (
    <div data-testid="admin-book-editor">
      <Link to="/admin/books" className="btn-ghost mb-4" data-testid="editor-back-btn"><ArrowLeft size={12} /> Back to books</Link>
      <PageHeader
        eyebrow={isNew ? "New Book" : "Edit Book"}
        title={book.title || "Untitled draft"}
        note={isNew ? "Every new book is saved as a Draft. Publishing requires the Owner." : `Slug: ${book.slug} · Workflow: ${book.publish_status}`}
      />

      {hasPlaceholder() && (
        <div className="mt-6 p-4 border-l-2 flex items-start gap-3" style={{ borderColor: "var(--gi-gold)", background: "var(--gi-ivory-2)" }} data-testid="placeholder-warning">
          <AlertTriangle size={16} style={{ color: "#8A6A00", marginTop: 2 }} />
          <div className="text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
            This entry contains editable placeholders. Replace them with verified content before publication.
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-8">
          <Card title="Principal Details">
            <Row>
              <Field label="Title *" value={book.title} onChange={v => set("title", v)} testId="editor-title" />
              <Field label="Slug (URL) *" value={book.slug} onChange={v => set("slug", v)} testId="editor-slug" />
            </Row>
            <Row>
              <Field label="Subtitle" value={book.subtitle || ""} onChange={v => set("subtitle", v)} testId="editor-subtitle" />
              <SelectField label="Series" value={book.series} onChange={v => set("series", v)} options={SERIES} testId="editor-series" />
            </Row>
            <Row>
              <SelectField label="Editorial Status" value={book.status} onChange={v => set("status", v)} options={STATUSES} testId="editor-status" />
              <SelectField label="Cover Accent" value={book.cover_accent} onChange={v => set("cover_accent", v)} options={ACCENTS} testId="editor-accent" />
            </Row>
            <Row>
              <Field label="Author (co-authors optional)" value="Dinesh Chandra Rawat" disabled />
              <Field label="Co-author or contributor (verified only)" value={book.co_author || ""} onChange={v => set("co_author", v)} testId="editor-coauthor" />
            </Row>
            <div>
              <label className="gi-label">Featured on homepage</label>
              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                <input type="checkbox" checked={!!book.is_featured} onChange={e => set("is_featured", e.target.checked)} data-testid="editor-featured" /> Yes, show in Featured Books
              </label>
            </div>
            <MultiSelect label="Subject Categories" options={SUBJECTS} value={book.topics} onChange={v => set("topics", v)} testId="editor-topics" />
          </Card>

          <Card title="Descriptions">
            <TextArea label="Short Description" rows={3} value={book.short_description} onChange={v => set("short_description", v)} testId="editor-short-desc" />
            <TextArea label="Detailed Description" rows={6} value={book.long_description} onChange={v => set("long_description", v)} testId="editor-long-desc" />
          </Card>

          <Card title="Advance reservation" note="Enable this to show a non-binding 'Reserve Print Copy' button on the public book page. Do not collect payment or promise dates through this form.">
            <div>
              <label className="flex items-center gap-2 text-sm" style={{ color: "var(--gi-charcoal-2)" }}>
                <input type="checkbox" checked={!!book.reservations_enabled} onChange={e => set("reservations_enabled", e.target.checked)} data-testid="editor-reservations-enabled" />
                Enable print reservations for this book
              </label>
            </div>
            {book.reservations_enabled && (
              <>
                <Row>
                  <Field label="CTA label (defaults to 'Reserve Print Copy')" value={book.reservation_cta_label || ""} onChange={v => set("reservation_cta_label", v)} testId="editor-reservation-cta" />
                  <SelectField
                    label="Availability status"
                    value={book.reservation_availability_status || "available"}
                    onChange={v => set("reservation_availability_status", v)}
                    options={["available", "limited", "waitlist", "closed"]}
                    testId="editor-reservation-status"
                  />
                </Row>
                <Row>
                  <div>
                    <label className="gi-label">Reservation opens at</label>
                    <input type="datetime-local" className="gi-input" value={(book.reservation_opens_at || "").slice(0, 16)} onChange={e => set("reservation_opens_at", e.target.value)} data-testid="editor-reservation-opens" />
                  </div>
                  <div>
                    <label className="gi-label">Reservation closes at</label>
                    <input type="datetime-local" className="gi-input" value={(book.reservation_closes_at || "").slice(0, 16)} onChange={e => set("reservation_closes_at", e.target.value)} data-testid="editor-reservation-closes" />
                  </div>
                </Row>
                <TextArea
                  label="Expected release note (shown above the reservation form)"
                  rows={2}
                  value={book.reservation_expected_release_note || ""}
                  onChange={v => set("reservation_expected_release_note", v)}
                  testId="editor-reservation-note"
                />
                <p className="text-xs" style={{ color: "var(--gi-muted)" }}>
                  The public form shows this notice: <em>"This is a non-binding reservation, not a completed purchase. Price, availability, shipping charges and delivery schedule will be confirmed before payment."</em>
                </p>
              </>
            )}
          </Card>

          <Card title="Language Editions" note="A principal title may have multiple language editions. Each carries its own cover, subtitle, description, ISBN, sample PDF, trailer and purchase links.">
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map(l => (
                <button key={l} type="button" onClick={() => toggleEdition(l)}
                  className="px-3 py-2 text-xs border" data-testid={`editor-edition-toggle-${l.toLowerCase()}`}
                  style={{
                    borderColor: book.editions.includes(l) ? "var(--gi-maroon)" : "var(--gi-border)",
                    background: book.editions.includes(l) ? "var(--gi-maroon)" : "#fff",
                    color: book.editions.includes(l) ? "#fff" : "var(--gi-charcoal)",
                  }}
                >{book.editions.includes(l) ? "✓ " : ""}{l}</button>
              ))}
            </div>
            {book.editions.length > 1 && (
              <>
                <div className="mt-6 flex gap-2 border-b" style={{ borderColor: "var(--gi-border)" }}>
                  {book.editions.map(l => (
                    <button key={l} type="button" onClick={() => setActiveEditionTab(l)}
                      className="px-4 py-2 text-xs font-medium border-b-2"
                      style={{ borderColor: activeEditionTab === l ? "var(--gi-maroon)" : "transparent", color: activeEditionTab === l ? "var(--gi-maroon)" : "var(--gi-muted)" }}
                      data-testid={`editor-edition-tab-${l.toLowerCase()}`}>{l}</button>
                  ))}
                </div>
                <div className="mt-6 space-y-4" data-testid={`editor-edition-panel-${activeEditionTab.toLowerCase()}`}>
                  <p className="text-xs" style={{ color: "var(--gi-muted)" }}>Fill only publisher-approved content. Do not machine-translate.</p>
                  <Row>
                    <Field label={`${activeEditionTab} title`} value={book.editions_meta?.[activeEditionTab]?.title || ""} onChange={v => setEditionMeta(activeEditionTab, "title", v)} />
                    <Field label={`${activeEditionTab} subtitle`} value={book.editions_meta?.[activeEditionTab]?.subtitle || ""} onChange={v => setEditionMeta(activeEditionTab, "subtitle", v)} />
                  </Row>
                  <TextArea label={`${activeEditionTab} short description`} rows={3} value={book.editions_meta?.[activeEditionTab]?.short_description || ""} onChange={v => setEditionMeta(activeEditionTab, "short_description", v)} />
                  <TextArea label={`${activeEditionTab} long description`} rows={5} value={book.editions_meta?.[activeEditionTab]?.long_description || ""} onChange={v => setEditionMeta(activeEditionTab, "long_description", v)} />
                  <Row>
                    <Field label={`ISBN`} value={book.editions_meta?.[activeEditionTab]?.isbn || ""} onChange={v => setEditionMeta(activeEditionTab, "isbn", v)} />
                    <SelectField label="Cover accent" value={book.editions_meta?.[activeEditionTab]?.cover_accent || book.cover_accent} onChange={v => setEditionMeta(activeEditionTab, "cover_accent", v)} options={ACCENTS} />
                  </Row>
                  <Row>
                    <Field label="Amazon URL" value={book.editions_meta?.[activeEditionTab]?.amazon_url || ""} onChange={v => setEditionMeta(activeEditionTab, "amazon_url", v)} />
                    <Field label="Green Mall URL" value={book.editions_meta?.[activeEditionTab]?.green_mall_url || ""} onChange={v => setEditionMeta(activeEditionTab, "green_mall_url", v)} />
                  </Row>
                  <Row>
                    <Field label="Goodreads URL" value={book.editions_meta?.[activeEditionTab]?.goodreads_url || ""} onChange={v => setEditionMeta(activeEditionTab, "goodreads_url", v)} />
                    <Field label="Sample PDF URL" value={book.editions_meta?.[activeEditionTab]?.sample_pdf_url || ""} onChange={v => setEditionMeta(activeEditionTab, "sample_pdf_url", v)} />
                  </Row>
                  <Row>
                    <Field label="Trailer URL" value={book.editions_meta?.[activeEditionTab]?.trailer_url || ""} onChange={v => setEditionMeta(activeEditionTab, "trailer_url", v)} />
                    <SelectField label="Edition status" value={book.editions_meta?.[activeEditionTab]?.status || book.status} onChange={v => setEditionMeta(activeEditionTab, "status", v)} options={STATUSES} />
                  </Row>
                </div>
              </>
            )}
          </Card>

          <Card title="Reading Content">
            <ListEditor label="Table of Contents" items={book.table_of_contents} onChange={v => set("table_of_contents", v)} testId="editor-toc" />
            <ListEditor label="Why Read This Book (scholarly)" items={book.why_read} onChange={v => set("why_read", v)} testId="editor-why-read" />
            <ListEditor label="What Readers Will Learn (practical)" items={book.what_readers_learn} onChange={v => set("what_readers_learn", v)} testId="editor-learn" />
            <ListEditor label="Practical Topics Covered" items={book.practical_topics} onChange={v => set("practical_topics", v)} testId="editor-practical" />
            <ListEditor label="Research Themes" items={book.research_themes} onChange={v => set("research_themes", v)} testId="editor-research-themes" />
            <ListEditor label="Selected Quotations" items={book.quotations} onChange={v => set("quotations", v)} testId="editor-quotations" />
          </Card>

          <Card title="Verified Reviews" note="Reviews are shown on the public site only when saved and published. Do not paste unverified reviews.">
            <ReviewsEditor value={book.reviews} onChange={v => set("reviews", v)} />
          </Card>

          <Card title="Purchase & Sample Links (default edition)">
            <p className="text-xs mb-3" style={{ color: "var(--gi-muted)" }}>Per-edition links are set inside the Language Editions block above.</p>
            <Row>
              <Field label="Amazon URL" value={book.amazon_url || ""} onChange={v => set("amazon_url", v)} testId="editor-amazon" />
              <Field label="Green Mall URL" value={book.green_mall_url || ""} onChange={v => set("green_mall_url", v)} testId="editor-green-mall" />
            </Row>
            <Row>
              <Field label="Goodreads URL" value={book.goodreads_url || ""} onChange={v => set("goodreads_url", v)} testId="editor-goodreads" />
              <Field label="Sample PDF URL" value={book.sample_pdf_url || ""} onChange={v => set("sample_pdf_url", v)} testId="editor-sample-pdf" />
            </Row>
            <Row>
              <Field label="Trailer URL" value={book.trailer_url || ""} onChange={v => set("trailer_url", v)} testId="editor-trailer" />
              <Field label="Trailer thumbnail URL" value={book.trailer_thumbnail_url || ""} onChange={v => set("trailer_thumbnail_url", v)} />
            </Row>
            <TextArea label="Bulk & Institutional Order Info" rows={3} value={book.bulk_order_info || ""} onChange={v => set("bulk_order_info", v)} testId="editor-bulk-info" />
          </Card>

          <Card title="Cover Image" note="Choose from the Media Library. Uploading is done in Media Library → Book covers.">
            <div className="grid gap-3 grid-cols-4">
              <label className="border p-3 cursor-pointer flex flex-col items-center gap-2 text-xs" style={{ borderColor: !book.cover_url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
                <input type="radio" checked={!book.cover_url} onChange={() => set("cover_url", "")} data-testid="editor-cover-placeholder" className="sr-only" />
                <div className="w-full aspect-[2/3] bg-[var(--gi-ivory-2)] flex items-center justify-center"><ImageIcon size={20} style={{ color: "var(--gi-muted)" }} /></div>
                Placeholder
              </label>
              {covers.slice(0, 7).map(c => (
                <label key={c.id} className="border p-3 cursor-pointer flex flex-col items-center gap-2 text-xs" style={{ borderColor: book.cover_url === c.url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
                  <input type="radio" checked={book.cover_url === c.url} onChange={() => set("cover_url", c.url)} className="sr-only" />
                  <img src={mediaUrl(c.url)} alt={c.alt_text} className="w-full aspect-[2/3] object-cover" />
                  <span className="truncate w-full">{c.filename}</span>
                </label>
              ))}
            </div>
            <Link to="/admin/media" className="btn-ghost mt-4" data-testid="editor-goto-media">Manage media library →</Link>
          </Card>

          <Card title="SEO">
            <Row>
              <Field label="SEO title" value={book.seo_title || ""} onChange={v => set("seo_title", v)} testId="editor-seo-title" />
              <Field label="Canonical URL" value={book.seo_canonical_url || ""} onChange={v => set("seo_canonical_url", v)} />
            </Row>
            <TextArea label="Meta description" rows={2} value={book.seo_meta_description || ""} onChange={v => set("seo_meta_description", v)} testId="editor-seo-desc" />
            <Field label="Social-sharing image URL" value={book.seo_social_image || ""} onChange={v => set("seo_social_image", v)} />
          </Card>

          <Card title="Interface Language Translations (page-level)" note="Enter approved translations only. Automatic translation is not used.">
            {["hi", "bn"].map(l => (
              <div key={l} className="border-t pt-4" style={{ borderColor: "var(--gi-divider)" }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading text-lg" style={{ color: "var(--gi-maroon)" }}>{l === "hi" ? "हिन्दी (Hindi)" : "বাংলা (Bengali)"}</div>
                  <SelectField label="" value={book.translations?.[l]?.status || "draft"} onChange={v => setTranslation(l, "status", v)} options={["draft", "under_review", "published"]} testId={`editor-trans-status-${l}`} />
                </div>
                <Row>
                  <Field label={`${l} title`} value={book.translations?.[l]?.title || ""} onChange={v => setTranslation(l, "title", v)} testId={`editor-trans-title-${l}`} />
                  <Field label={`${l} subtitle`} value={book.translations?.[l]?.subtitle || ""} onChange={v => setTranslation(l, "subtitle", v)} />
                </Row>
                <TextArea label={`${l} short description`} rows={2} value={book.translations?.[l]?.short_description || ""} onChange={v => setTranslation(l, "short_description", v)} />
              </div>
            ))}
          </Card>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="border bg-white p-6 sticky top-6" style={{ borderColor: "var(--gi-border)" }}>
            <div className="font-accent text-[10px] mb-3" style={{ color: "var(--gi-maroon)" }}>Workflow</div>
            <StatusPill status={book.publish_status || "draft"} />
            {book.published_at && <div className="mt-2 text-xs" style={{ color: "var(--gi-muted)" }}>Published {new Date(book.published_at).toLocaleString()}</div>}
            {!isReviewer && (
              <>
                <button onClick={save} disabled={saving} className="btn-maroon w-full justify-center mt-6" data-testid="editor-save-btn"><Save size={12} /> {saving ? "Saving…" : (isNew ? "Save draft" : "Save changes")}</button>
                {!isNew && isOwner && book.publish_status !== "published" && (
                  <button onClick={doPublish} className="btn-gold w-full justify-center mt-3" data-testid="editor-publish-btn"><Eye size={12} /> Publish</button>
                )}
                {!isNew && isOwner && book.publish_status === "published" && (
                  <button onClick={doUnpublish} className="btn-outline w-full justify-center mt-3" data-testid="editor-unpublish-btn"><EyeOff size={12} /> Unpublish</button>
                )}
                {!isNew && (
                  <button onClick={doArchive} className="btn-ghost w-full justify-center mt-3" data-testid="editor-archive-btn"><Archive size={12} /> Archive</button>
                )}
              </>
            )}
            {!isNew && (
              <a href={`/books/${book.slug}`} target="_blank" rel="noreferrer" className="btn-ghost w-full justify-center mt-3" data-testid="editor-preview-btn">Preview public page →</a>
            )}
            {isReviewer && <p className="mt-4 text-xs" style={{ color: "var(--gi-muted)" }}>You are signed in as a Reviewer. Editing is disabled.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}

const Card = ({ title, note, children }) => (
  <section className="border bg-white" style={{ borderColor: "var(--gi-border)" }}>
    <div className="px-6 py-4 border-b" style={{ borderColor: "var(--gi-border)" }}>
      <div className="font-heading text-xl" style={{ color: "var(--gi-maroon)" }}>{title}</div>
      {note && <p className="mt-1 text-xs" style={{ color: "var(--gi-muted)" }}>{note}</p>}
    </div>
    <div className="p-6 space-y-4">{children}</div>
  </section>
);
const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, value, onChange, disabled, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <input className="gi-input" value={value} onChange={e => onChange && onChange(e.target.value)} disabled={disabled} data-testid={testId} />
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3, testId }) => (
  <div>
    <label className="gi-label">{label}</label>
    <textarea rows={rows} className="gi-input" value={value} onChange={e => onChange(e.target.value)} data-testid={testId} />
  </div>
);
const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    {label && <label className="gi-label">{label}</label>}
    <select className="gi-input" value={value} onChange={e => onChange(e.target.value)} data-testid={testId}>
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  </div>
);
const MultiSelect = ({ label, options, value, onChange, testId }) => (
  <div data-testid={testId}>
    <label className="gi-label">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map(o => {
        const on = value.includes(o);
        return <button key={o} type="button" onClick={() => onChange(on ? value.filter(x => x !== o) : [...value, o])}
          className="px-3 py-1.5 text-xs border" style={{ borderColor: on ? "var(--gi-maroon)" : "var(--gi-border)", background: on ? "var(--gi-maroon)" : "#fff", color: on ? "#fff" : "var(--gi-charcoal-2)" }}>{o}</button>;
      })}
    </div>
  </div>
);
const ListEditor = ({ label, items, onChange, testId }) => (
  <div data-testid={testId}>
    <label className="gi-label">{label}</label>
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={`${testId}-${i}`} className="flex gap-2">
          <input className="gi-input flex-1" value={it} onChange={e => onChange(items.map((x, j) => j === i ? e.target.value : x))} />
          <button type="button" className="btn-ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
        </div>
      ))}
      <button type="button" className="btn-ghost" onClick={() => onChange([...items, ""])}><Plus size={12} /> Add item</button>
    </div>
  </div>
);
const ReviewsEditor = ({ value = [], onChange }) => {
  const setAt = (i, k, v) => onChange(value.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <div className="space-y-4">
      {value.map((r, i) => (
        <div key={`review-${i}-${r.reviewer || "unnamed"}`} className="border p-4" style={{ borderColor: "var(--gi-border)" }}>
          <Row>
            <Field label="Reviewer name" value={r.reviewer || ""} onChange={v => setAt(i, "reviewer", v)} />
            <Field label="Reviewer credential (verified)" value={r.credential || ""} onChange={v => setAt(i, "credential", v)} />
          </Row>
          <TextArea label="Quote" rows={3} value={r.quote || ""} onChange={v => setAt(i, "quote", v)} />
          <button type="button" className="btn-ghost mt-2" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={12} /> Remove</button>
        </div>
      ))}
      <button type="button" className="btn-outline" onClick={() => onChange([...value, { reviewer: "", credential: "", quote: "" }])}><Plus size={12} /> Add verified review</button>
    </div>
  );
};
const StatusPill = ({ status }) => {
  const map = {
    draft: { bg: "var(--gi-ivory-2)", fg: "var(--gi-maroon)", label: "Draft" },
    published: { bg: "#E6F1E7", fg: "#2E5D34", label: "Published" },
    archived: { bg: "#F1E4E4", fg: "#7A2E2E", label: "Archived" },
  };
  const s = map[status] || map.draft;
  return <span className="inline-block px-3 py-1 text-[10px]" style={{ background: s.bg, color: s.fg, letterSpacing: "0.14em", textTransform: "uppercase" }} data-testid={`editor-workflow-status-${status}`}>{s.label}</span>;
};
